import { REQUEST, SUCCESS, FAILURE } from 'redux-config/reduxHelpers';
import { getDashboardAggregations } from 'common/dashboards/metrics';
import * as ActionTypes from './actions';

export const initialState = {
  currentMonthFunnel: {},
  currentMonthFunnelRequestId: null,
  currentMonthFunnelLoadedAggregations: {},
  currentMonthFunnelAreLoaded: false,
  lastMonthFunnel: {},
  lastMonthFunnelRequestId: null,
  lastMonthFunnelLoadedAggregations: {},
  lastMonthFunnelAreLoaded: false,
  firstMonthToCompare: {},
  firstMonthToCompareRequestId: null,
  firstMonthToCompareLoadedAggregations: {},
  firstMonthToCompareAreLoaded: false,
  secondMonthToCompare: {},
  secondMonthToCompareRequestId: null,
  secondMonthToCompareLoadedAggregations: {},
  secondMonthToCompareAreLoaded: false,
  target: {},
  targetIsLoaded: false,
  usersHistory: {},
  usersHistoryIsLoaded: false,
  totals: {},
  totalsIsLoaded: false,
  boHistory: {},
  boHistoryIsLoaded: false,
};

const mergeFunnelData = (previous, incoming) => {
  const dailyData = { ...previous.aggregated_data_by_day };
  Object.entries(incoming.aggregated_data_by_day || {}).forEach(([day, metrics]) => {
    dailyData[day] = { ...dailyData[day], ...metrics };
  });
  return {
    ...previous,
    ...incoming,
    aggregated_data: { ...previous.aggregated_data, ...incoming.aggregated_data },
    aggregated_data_by_day: dailyData,
  };
};

const reduceFunnel = (state, action, field, phase) => {
  const requestIdField = `${field}RequestId`;
  const loadedField = `${field}LoadedAggregations`;
  const allLoadedField = `${field}AreLoaded`;
  if (phase === REQUEST) {
    const aggregations = action.aggregations || getDashboardAggregations(action.sampleType);
    return {
      ...state,
      [field]: {},
      [requestIdField]: action.requestId,
      [loadedField]: Object.fromEntries(aggregations.map(aggregation => [aggregation, false])),
      [allLoadedField]: false,
    };
  }
  if (action.requestId !== state[requestIdField]) {
    return state;
  }
  if (phase === FAILURE) {
    return { ...state, error: action.error, [allLoadedField]: false };
  }
  const loadedAggregations = { ...state[loadedField], [action.aggregation]: true };
  return {
    ...state,
    [field]: mergeFunnelData(state[field], action.data),
    [loadedField]: loadedAggregations,
    [allLoadedField]: Object.values(loadedAggregations).every(Boolean),
  };
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.GET_CURRENT_MONTH_FUNNELS[REQUEST]:
      return reduceFunnel(state, action, 'currentMonthFunnel', REQUEST);
    case ActionTypes.GET_CURRENT_MONTH_FUNNELS[SUCCESS]:
      return reduceFunnel(state, action, 'currentMonthFunnel', SUCCESS);
    case ActionTypes.GET_CURRENT_MONTH_FUNNELS[FAILURE]:
      return reduceFunnel(state, action, 'currentMonthFunnel', FAILURE);
    case ActionTypes.GET_LAST_MONTH_FUNNELS[REQUEST]:
      return reduceFunnel(state, action, 'lastMonthFunnel', REQUEST);
    case ActionTypes.GET_LAST_MONTH_FUNNELS[SUCCESS]:
      return reduceFunnel(state, action, 'lastMonthFunnel', SUCCESS);
    case ActionTypes.GET_LAST_MONTH_FUNNELS[FAILURE]:
      return reduceFunnel(state, action, 'lastMonthFunnel', FAILURE);
    case ActionTypes.GET_FIRST_MONTH_TO_COMPARE[REQUEST]:
      return reduceFunnel(state, action, 'firstMonthToCompare', REQUEST);
    case ActionTypes.GET_FIRST_MONTH_TO_COMPARE[SUCCESS]:
      return reduceFunnel(state, action, 'firstMonthToCompare', SUCCESS);
    case ActionTypes.GET_FIRST_MONTH_TO_COMPARE[FAILURE]:
      return reduceFunnel(state, action, 'firstMonthToCompare', FAILURE);
    case ActionTypes.GET_SECOND_MONTH_TO_COMPARE[REQUEST]:
      return reduceFunnel(state, action, 'secondMonthToCompare', REQUEST);
    case ActionTypes.GET_SECOND_MONTH_TO_COMPARE[SUCCESS]:
      return reduceFunnel(state, action, 'secondMonthToCompare', SUCCESS);
    case ActionTypes.GET_SECOND_MONTH_TO_COMPARE[FAILURE]:
      return reduceFunnel(state, action, 'secondMonthToCompare', FAILURE);

    case ActionTypes.GET_TARGET[REQUEST]:
      return { ...state, target: {}, targetIsLoaded: false };
    case ActionTypes.GET_TARGET[SUCCESS]:
      return { ...state, target: { ...state.target, ...action.data }, targetIsLoaded: true };
    case ActionTypes.GET_TARGET[FAILURE]:
      return { ...state, error: action.error, targetIsLoaded: false };

    case ActionTypes.GET_USERS_HISTORY[REQUEST]:
      return { ...state, usersHistory: {}, usersHistoryIsLoaded: false };
    case ActionTypes.GET_USERS_HISTORY[SUCCESS]:
      return { ...state, usersHistory: { ...action.data }, usersHistoryIsLoaded: true };
    case ActionTypes.GET_USERS_HISTORY[FAILURE]:
      return { ...state, error: action.error, usersHistoryIsLoaded: false };

    case ActionTypes.GET_TOTALS[REQUEST]:
      return { ...state, totals: {}, totalsIsLoaded: false };
    case ActionTypes.GET_TOTALS[SUCCESS]:
      return { ...state, totals: { ...action.data }, totalsIsLoaded: true };
    case ActionTypes.GET_TOTALS[FAILURE]:
      return { ...state, error: action.error, totalsIsLoaded: false };

    case ActionTypes.GET_BACKOFFICE_HISTORY[REQUEST]:
      return { ...state, boHistoryIsLoaded: false };
    case ActionTypes.GET_BACKOFFICE_HISTORY[SUCCESS]:
      return { ...state, boHistory: { ...action.data }, boHistoryIsLoaded: true };
    case ActionTypes.GET_BACKOFFICE_HISTORY[FAILURE]:
      return { ...state, error: action.error, boHistoryIsLoaded: false };

    case ActionTypes.RESET_DASHBOARD_LOADED_FLAGS:
      return {
        ...state,
        currentMonthFunnel: {},
        currentMonthFunnelRequestId: null,
        currentMonthFunnelLoadedAggregations: {},
        currentMonthFunnelAreLoaded: false,
        targetIsLoaded: false,
        lastMonthFunnel: {},
        lastMonthFunnelRequestId: null,
        lastMonthFunnelLoadedAggregations: {},
        lastMonthFunnelAreLoaded: false,
      };

    case ActionTypes.RESET_DASHBOARD_FIRST_AND_SECOND_MONTHS_FUNNELS:
      return {
        ...state,
        firstMonthToCompare: {},
        firstMonthToCompareRequestId: null,
        firstMonthToCompareLoadedAggregations: {},
        firstMonthToCompareAreLoaded: false,
        secondMonthToCompare: {},
        secondMonthToCompareRequestId: null,
        secondMonthToCompareLoadedAggregations: {},
        secondMonthToCompareAreLoaded: false,
      };

    default:
      return state;
  }
};

export default reducer;
